import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2, GripVertical, Zap, Check, Search } from 'lucide-react';
import {
  ScannerConfig,
  ScannerFilter,
  ScannerColumn,
  ScannerSortConfig,
  ScannerType,
  ScannerStrategy,
  FilterOperator,
  ColumnFormat,
} from '@/services/scannerService';
import {
  StrategyConfig,
  StrategyCategory,
  STRATEGY_COLORS,
  STRATEGY_CATEGORIES,
  strategyApiService,
} from '@/services/strategyConfigService';

// ── Available filter fields with metadata ──
const FILTER_FIELDS = [
  { field: 'Price', type: 'number', label: 'Price' },
  { field: 'Change', type: 'number', label: 'Change ($)' },
  { field: 'ChangePercent', type: 'number', label: 'Change (%)' },
  { field: 'ChangePercent2Min', type: 'number', label: '2-Min Change (%)' },
  { field: 'Volume', type: 'number', label: 'Volume' },
  { field: 'RelativeVolume', type: 'number', label: 'Relative Volume' },
  { field: 'RelativeVolumeTod', type: 'number', label: 'Rel Volume (time-of-day)' },
  { field: 'GapPercent', type: 'number', label: 'Gap (%)' },
  // "(0-100)" described the OLD divergent ScannerService formula (position within the day's
  // range). Both scanner paths now compute volatility potential — ATR not yet spent, as % of
  // price — so the range hint was actively misleading. VwapSlopeAtr was missing entirely, which meant
  // the field gating all six HOD/LOD presets could not be picked in the supported UI.
  { field: 'AtrRoomPct', type: 'number', label: 'Room% (unspent ATR)' },
  { field: 'VwapSlopeAtr', type: 'number', label: 'VWSlp (VWAP slope)' },
  { field: 'ATR', type: 'number', label: 'ATR' },
  { field: 'ATRPercent', type: 'number', label: 'ATR (%)' },
  { field: 'VWAP', type: 'number', label: 'VWAP' },
  { field: 'Float', type: 'number', label: 'Float' },
  { field: 'MarketCap', type: 'number', label: 'Market Cap' },
  { field: 'IsHalted', type: 'boolean', label: 'Is Halted' },
  // Live-only halt / news / LULD fields. The backend resolver accepts these, but without picker
  // entries the news- and pre-halt scanners they exist for were only buildable by hand.
  { field: 'HaltKind', type: 'string', label: 'Halt Reason Class' },
  { field: 'MinutesSinceResume', type: 'number', label: 'Minutes Since Resume' },
  { field: 'LimitUpPrice', type: 'number', label: 'Limit Up' },
  { field: 'LimitDownPrice', type: 'number', label: 'Limit Down' },
  { field: 'LuldProximityPct', type: 'number', label: '% to LULD Band' },
  { field: 'HasNews', type: 'boolean', label: 'Has News' },
  { field: 'NewsAgeMinutes', type: 'number', label: 'News Age (min)' },
];

/** The declared type of a pickable field; defaults to number for anything not listed. */
const fieldType = (field: string): string =>
  FILTER_FIELDS.find(f => f.field === field)?.type ?? 'number';

const OPERATORS: { value: FilterOperator; label: string }[] = [
  { value: 'GreaterThan', label: '>' },
  { value: 'GreaterThanOrEqual', label: '>=' },
  { value: 'LessThan', label: '<' },
  { value: 'LessThanOrEqual', label: '<=' },
  { value: 'Equals', label: '=' },
  { value: 'NotEquals', label: '!=' },
  { value: 'Between', label: 'Between' },
];

const COLUMN_FIELDS = [
  { field: 'Symbol', header: 'Symbol', format: 'Text' as ColumnFormat },
  { field: 'Price', header: 'Price', format: 'Currency' as ColumnFormat },
  { field: 'Change', header: 'Chg', format: 'Change' as ColumnFormat },
  { field: 'ChangePercent', header: 'Chg%', format: 'ChangePercent' as ColumnFormat },
  { field: 'ChangePercent2Min', header: '2m%', format: 'ChangePercent' as ColumnFormat },
  { field: 'Volume', header: 'Vol', format: 'Volume' as ColumnFormat },
  { field: 'RelativeVolume', header: 'RVol', format: 'Number' as ColumnFormat },
  { field: 'RelativeVolumeTod', header: 'RVolToD', format: 'Number' as ColumnFormat },
  { field: 'GapPercent', header: 'Gap%', format: 'Percent' as ColumnFormat },
  { field: 'AtrRoomPct', header: 'Room%', format: 'Number' as ColumnFormat },
  { field: 'ATR', header: 'ATR', format: 'Currency' as ColumnFormat },
  { field: 'ATRPercent', header: 'ATR%', format: 'Percent' as ColumnFormat },
  { field: 'VWAP', header: 'VWAP', format: 'Currency' as ColumnFormat },
  { field: 'Float', header: 'Float', format: 'Volume' as ColumnFormat },
  { field: 'MarketCap', header: 'MCap', format: 'Volume' as ColumnFormat },
  { field: 'High', header: 'High', format: 'Currency' as ColumnFormat },
  { field: 'Low', header: 'Low', format: 'Currency' as ColumnFormat },
  { field: 'Open', header: 'Open', format: 'Currency' as ColumnFormat },
];

const SCANNER_TYPES: { value: ScannerType; label: string }[] = [
  { value: 'Custom', label: 'Custom' },
  { value: 'TopGainers', label: 'Top Gainers' },
  { value: 'TopLosers', label: 'Top Losers' },
  { value: 'MostActive', label: 'Most Active' },
  { value: 'MomoHOD', label: 'Momentum HOD' },
  { value: 'MomoLOD', label: 'Momentum LOD' },
  { value: 'GapUp', label: 'Gap Up' },
  { value: 'GapDown', label: 'Gap Down' },
  { value: 'VolumeSpike', label: 'Volume Spike' },
  { value: 'MomoUp2Min', label: 'Momo Up 2-Min' },
  { value: 'MomoDown2Min', label: 'Momo Down 2-Min' },
  { value: 'NewHighs', label: 'New Highs' },
  { value: 'NewLows', label: 'New Lows' },
  { value: 'PreMarketGainers', label: 'Pre-Market Gainers' },
  { value: 'PreMarketLosers', label: 'Pre-Market Losers' },
];

const DEFAULT_COLUMNS = ['Symbol', 'Price', 'ChangePercent', 'Volume', 'RelativeVolume', 'GapPercent'];

interface CreateScannerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (config: ScannerConfig) => void;
  editScanner?: ScannerConfig | null;
}

export const CreateScannerDialog: React.FC<CreateScannerDialogProps> = ({
  open,
  onOpenChange,
  onCreated,
  editScanner,
}) => {
  const isEdit = !!editScanner;

  const [name, setName] = useState(editScanner?.name ?? '');
  const [description, setDescription] = useState(editScanner?.description ?? '');
  const [type, setType] = useState<ScannerType>(editScanner?.type ?? 'Custom');
  const [assetClass, setAssetClass] = useState<'Equity' | 'Forex' | 'Crypto'>(editScanner?.assetClass ?? 'Equity');
  const [maxResults, setMaxResults] = useState(editScanner?.maxResults ?? 25);
  const [filters, setFilters] = useState<ScannerFilter[]>(editScanner?.filters ?? []);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    editScanner?.columns?.map(c => c.field) ?? DEFAULT_COLUMNS
  );
  const [sortField, setSortField] = useState(editScanner?.sort?.field ?? 'ChangePercent');
  const [sortDesc, setSortDesc] = useState(editScanner?.sort?.descending ?? true);

  // ── Universe picker ── (E2E finding: the plan caps how many symbols one scanner
  // may cover, but this dialog had no way to narrow the universe — the cap's
  // "narrow the symbol list" advice was a dead end.)
  const [symbolLists, setSymbolLists] = useState<{ id: string; name: string; symbols: string[] }[]>([]);
  const [symbolListId, setSymbolListId] = useState<string | null>(editScanner?.symbolListId ?? null);
  // Whether the user actively changed the Universe select this open — an untouched
  // picker must never clear an existing inline symbol universe on unrelated edits.
  const [universeTouched, setUniverseTouched] = useState(false);
  useEffect(() => {
    if (!open) return;
    import('@/services/strategyService')
      .then(m => m.automationService.getSymbolLists())
      .then(setSymbolLists)
      .catch(() => setSymbolLists([]));
  }, [open]);

  // ── Strategy picker state ──
  const [availableStrategies, setAvailableStrategies] = useState<StrategyConfig[]>([]);
  const [selectedStrategies, setSelectedStrategies] = useState<ScannerStrategy[]>(
    editScanner?.strategies ?? []
  );
  const [strategySearch, setStrategySearch] = useState('');
  const [strategyCategoryFilter, setStrategyCategoryFilter] = useState<string>('all');
  const colorIndexRef = React.useRef(0);

  // Fetch available strategies (presets + saved) when dialog opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const load = async () => {
      try {
        const [presetsMap, saved] = await Promise.all([
          strategyApiService.getPresets(),
          strategyApiService.getSaved().catch(() => [] as StrategyConfig[]),
        ]);
        if (cancelled) return;
        const presets = Object.values(presetsMap).flat();
        // Merge: saved first, then presets not already in saved
        const savedIds = new Set(saved.map(s => s.id));
        const merged = [...saved, ...presets.filter(p => !savedIds.has(p.id))];
        setAvailableStrategies(merged);
      } catch (err) {
        console.error('Failed to load strategies:', err);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open]);

  const getNextColor = useCallback(() => {
    const c = STRATEGY_COLORS[colorIndexRef.current % STRATEGY_COLORS.length];
    colorIndexRef.current++;
    return c;
  }, []);

  const toggleStrategy = useCallback((strategy: StrategyConfig) => {
    setSelectedStrategies(prev => {
      const exists = prev.find(s => s.strategyId === strategy.id);
      if (exists) {
        return prev.filter(s => s.strategyId !== strategy.id);
      }
      return [
        ...prev,
        {
          strategyId: strategy.id,
          name: strategy.name,
          color: getNextColor(),
          enabled: true,
        },
      ];
    });
  }, [getNextColor]);

  const removeStrategy = useCallback((strategyId: string) => {
    setSelectedStrategies(prev => prev.filter(s => s.strategyId !== strategyId));
  }, []);

  const changeStrategyColor = useCallback((strategyId: string, color: string) => {
    setSelectedStrategies(prev =>
      prev.map(s => s.strategyId === strategyId ? { ...s, color } : s)
    );
  }, []);

  // Reset form when dialog opens with different scanner
  React.useEffect(() => {
    if (open) {
      setName(editScanner?.name ?? '');
      setDescription(editScanner?.description ?? '');
      setType(editScanner?.type ?? 'Custom');
      setAssetClass(editScanner?.assetClass ?? 'Equity');
      setMaxResults(editScanner?.maxResults ?? 25);
      setFilters(editScanner?.filters ?? []);
      setSelectedColumns(editScanner?.columns?.map(c => c.field) ?? DEFAULT_COLUMNS);
      setSortField(editScanner?.sort?.field ?? 'ChangePercent');
      setSortDesc(editScanner?.sort?.descending ?? true);
      setSelectedStrategies(editScanner?.strategies ?? []);
      setStrategySearch('');
      setStrategyCategoryFilter('all');
      setSymbolListId(editScanner?.symbolListId ?? null);
      setUniverseTouched(false);
      colorIndexRef.current = editScanner?.strategies?.length ?? 0;
    }
  }, [open, editScanner]);

  const addFilter = useCallback(() => {
    setFilters(prev => [...prev, { field: 'Price', operator: 'GreaterThan', value: 0 }]);
  }, []);

  const removeFilter = useCallback((index: number) => {
    setFilters(prev => prev.filter((_, i) => i !== index));
  }, []);

  const updateFilter = useCallback((index: number, updates: Partial<ScannerFilter>) => {
    setFilters(prev => prev.map((f, i) => i === index ? { ...f, ...updates } : f));
  }, []);

  const toggleColumn = useCallback((field: string) => {
    setSelectedColumns(prev =>
      prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field]
    );
  }, []);

  const handleSubmit = useCallback(() => {
    if (!name.trim()) return;

    const columns: ScannerColumn[] = selectedColumns.map((field, i) => {
      const meta = COLUMN_FIELDS.find(c => c.field === field)!;
      return {
        field: meta.field,
        header: meta.header,
        width: field === 'Symbol' ? 80 : 70,
        format: meta.format,
        visible: true,
        order: i,
      };
    });

    const sort: ScannerSortConfig = { field: sortField, descending: sortDesc };

    const config: ScannerConfig = {
      id: editScanner?.id ?? '',
      name: name.trim(),
      description: description.trim(),
      type,
      filters,
      columns,
      sort,
      maxResults,
      isActive: true,
      isPreset: false,
      createdAt: editScanner?.createdAt ?? new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
      strategies: selectedStrategies.length > 0 ? selectedStrategies : undefined,
      assetClass,
      // The backend treats a NONEMPTY inline SymbolList as the effective universe,
      // so an explicit "All tracked symbols" choice must clear it — otherwise the
      // selection silently doesn't take. An untouched picker preserves it as-is.
      symbolList: universeTouched && symbolListId === null ? [] : (editScanner?.symbolList ?? []),
      symbolListId,
    };

    onCreated(config);
    onOpenChange(false);
  }, [name, description, type, assetClass, filters, selectedColumns, sortField, sortDesc, maxResults, selectedStrategies, symbolListId, editScanner, onCreated, onOpenChange]);

  // NB: a Custom scanner with no strategies and no filters is a VALID all-symbols
  // feed (the evaluator's pure filter path passes every bar when the filter list is
  // empty) — an earlier guard here blocked it on a misdiagnosis; the E2E scanner
  // that sat empty had a universe that resolved to zero symbols, not missing alerts.

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Scanner' : 'Create Scanner'}</DialogTitle>
          <DialogDescription>
            {isEdit ? 'Modify scanner configuration' : 'Build a custom scanner with filters and columns'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* ── Basic info ── */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Name</label>
              <input
                className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="My Scanner"
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Type</label>
              <Select value={type} onValueChange={v => setType(v as ScannerType)}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCANNER_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Asset Class</label>
              <Select value={assetClass} onValueChange={v => setAssetClass(v as 'Equity' | 'Forex' | 'Crypto')}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Equity">Equity (stocks)</SelectItem>
                  <SelectItem value="Forex">Forex (currency pairs)</SelectItem>
                  <SelectItem value="Crypto">Crypto (coin pairs)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Description</label>
            <input
              className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="Optional description"
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </div>

          {/* ── Universe ── */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Universe</label>
            <Select value={symbolListId ?? '__all__'}
              onValueChange={v => { setSymbolListId(v === '__all__' ? null : v); setUniverseTouched(true); }}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All tracked symbols (~13k)</SelectItem>
                {/* Empty lists are unselectable-by-omission: the backend's named-universe
                    resolution rejects a list with zero symbols anyway. */}
                {symbolLists.filter(l => l.symbols.length > 0).map(l => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.name} — {l.symbols.length.toLocaleString()} symbols
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground/70">
              Plans cap how many symbols one scanner may cover — pick a symbol list to stay
              inside yours{symbolLists.length === 0 ? ' (you have no lists yet)' : ''}.
            </p>
          </div>

          {/* ── Filters ── */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-muted-foreground">Filters</label>
              <Button variant="ghost" size="sm" onClick={addFilter} className="h-7 text-xs">
                <Plus className="h-3 w-3 mr-1" /> Add Filter
              </Button>
            </div>

            {filters.length === 0 && (
              <p className="text-xs text-muted-foreground/60 italic">No filters — all symbols will match</p>
            )}

            {filters.map((filter, i) => (
              <div key={i} className="flex items-center gap-2 bg-muted/30 rounded-md p-2">
                <GripVertical className="h-3 w-3 text-muted-foreground/40 shrink-0" />

                <Select value={filter.field} onValueChange={v => updateFilter(i, { field: v })}>
                  <SelectTrigger className="h-8 w-[140px] text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FILTER_FIELDS.map(f => (
                      <SelectItem key={f.field} value={f.field}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filter.operator} onValueChange={v => updateFilter(i, { operator: v as FilterOperator })}>
                  <SelectTrigger className="h-8 w-[100px] text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {OPERATORS.map(op => (
                      <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* The editor follows the FIELD'S declared type. A number box for everything
                    meant HaltKind (a string) could not be given "News"/"Volatility" at all, and
                    HasNews was sent as 1/0 which never compares equal to the backend's boolean —
                    so halt/news scanners built here silently matched nothing. */}
                {fieldType(filter.field) === 'boolean' ? (
                  <Select
                    value={filter.value === true ? 'true' : 'false'}
                    onValueChange={v => updateFilter(i, { value: v === 'true' })}
                  >
                    <SelectTrigger className="h-8 w-[90px] text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">true</SelectItem>
                      <SelectItem value="false">false</SelectItem>
                    </SelectContent>
                  </Select>
                ) : fieldType(filter.field) === 'string' ? (
                  <input
                    type="text"
                    className="h-8 w-[120px] px-2 rounded-md border border-input bg-background text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                    value={typeof filter.value === 'string' ? filter.value : ''}
                    onChange={e => updateFilter(i, { value: e.target.value })}
                    placeholder={filter.field === 'HaltKind' ? 'News / Volatility' : 'Value'}
                  />
                ) : (
                  <input
                    type="number"
                    className="h-8 w-[90px] px-2 rounded-md border border-input bg-background text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                    value={typeof filter.value === 'number' ? filter.value : ''}
                    onChange={e => updateFilter(i, { value: parseFloat(e.target.value) || 0 })}
                    placeholder="Value"
                  />
                )}

                {filter.operator === 'Between' && fieldType(filter.field) === 'number' && (
                  <input
                    type="number"
                    className="h-8 w-[90px] px-2 rounded-md border border-input bg-background text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                    value={typeof filter.value2 === 'number' ? filter.value2 : ''}
                    onChange={e => updateFilter(i, { value2: parseFloat(e.target.value) || 0 })}
                    placeholder="Max"
                  />
                )}

                <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => removeFilter(i)}>
                  <Trash2 className="h-3 w-3 text-red-400" />
                </Button>
              </div>
            ))}
          </div>

          {/* ── Strategies ── */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <Zap className="h-3 w-3 text-yellow-500" />
                Strategies
                {selectedStrategies.length > 0 && (
                  <Badge variant="secondary" className="text-[9px] h-4 px-1 ml-1">
                    {selectedStrategies.length} selected
                  </Badge>
                )}
              </label>
            </div>

            {/* Selected strategies with colors */}
            {selectedStrategies.length > 0 && (
              <div className="space-y-1">
                {selectedStrategies.map(ss => (
                  <div
                    key={ss.strategyId}
                    className="flex items-center gap-2 bg-muted/30 rounded-md px-2 py-1.5 border-l-2"
                    style={{ borderLeftColor: ss.color }}
                  >
                    {/* Color picker */}
                    <input
                      type="color"
                      className="h-5 w-5 rounded cursor-pointer border-0 bg-transparent p-0"
                      value={ss.color}
                      onChange={e => changeStrategyColor(ss.strategyId, e.target.value)}
                      title="Change color"
                    />
                    <span className="text-xs font-medium flex-1 truncate">{ss.name}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      onClick={() => removeStrategy(ss.strategyId)}
                    >
                      <Trash2 className="h-3 w-3 text-red-400" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Search & category filter */}
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                <input
                  className="w-full h-8 pl-7 pr-2 rounded-md border border-input bg-background text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                  placeholder="Search strategies..."
                  value={strategySearch}
                  onChange={e => setStrategySearch(e.target.value)}
                />
              </div>
              <Select value={strategyCategoryFilter} onValueChange={setStrategyCategoryFilter}>
                <SelectTrigger className="h-8 w-[120px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {STRATEGY_CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.icon} {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Available strategies list */}
            <div className="max-h-[160px] overflow-auto rounded-md border border-border divide-y divide-border">
              {availableStrategies.length === 0 ? (
                <p className="text-xs text-muted-foreground/60 italic p-3 text-center">
                  Loading strategies...
                </p>
              ) : (
                availableStrategies
                  .filter(s => {
                    const matchesSearch =
                      !strategySearch ||
                      s.name.toLowerCase().includes(strategySearch.toLowerCase()) ||
                      s.description?.toLowerCase().includes(strategySearch.toLowerCase());
                    const matchesCategory =
                      strategyCategoryFilter === 'all' || s.category === strategyCategoryFilter;
                    return matchesSearch && matchesCategory;
                  })
                  .map(strategy => {
                    const isSelected = selectedStrategies.some(ss => ss.strategyId === strategy.id);
                    const meta = STRATEGY_CATEGORIES.find(c => c.value === strategy.category);
                    return (
                      <div
                        key={strategy.id}
                        className={`flex items-center gap-2 px-2.5 py-1.5 cursor-pointer transition-colors hover:bg-muted/30 ${
                          isSelected ? 'bg-primary/5' : ''
                        }`}
                        onClick={() => toggleStrategy(strategy)}
                      >
                        <div
                          className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                            isSelected
                              ? 'bg-primary border-primary'
                              : 'border-muted-foreground/30'
                          }`}
                        >
                          {isSelected && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                        </div>
                        <span className="text-xs">{meta?.icon}</span>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-medium truncate">{strategy.name}</div>
                          {strategy.description && (
                            <div className="text-[10px] text-muted-foreground/60 truncate">
                              {strategy.description}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {strategy.alerts.length > 0 && (
                            <Badge variant="outline" className="text-[9px] h-4 px-1">
                              {strategy.alerts.length} alerts
                            </Badge>
                          )}
                          {strategy.filters.length > 0 && (
                            <Badge variant="outline" className="text-[9px] h-4 px-1">
                              {strategy.filters.length} filters
                            </Badge>
                          )}
                          {!strategy.isPreset && (
                            <Badge variant="secondary" className="text-[9px] h-4 px-1">
                              saved
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  })
              )}
            </div>
          </div>

          {/* ── Columns ── */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground">Columns</label>
            <div className="flex flex-wrap gap-1.5">
              {COLUMN_FIELDS.map(col => (
                <Badge
                  key={col.field}
                  variant={selectedColumns.includes(col.field) ? 'default' : 'outline'}
                  className="cursor-pointer select-none text-xs"
                  onClick={() => toggleColumn(col.field)}
                >
                  {col.header}
                </Badge>
              ))}
            </div>
          </div>

          {/* ── Sort & Max ── */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Sort By</label>
              <Select value={sortField} onValueChange={setSortField}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COLUMN_FIELDS.map(c => (
                    <SelectItem key={c.field} value={c.field}>{c.header}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Order</label>
              <Select value={sortDesc ? 'desc' : 'asc'} onValueChange={v => setSortDesc(v === 'desc')}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="desc">Descending</SelectItem>
                  <SelectItem value="asc">Ascending</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Max Results</label>
              <input
                type="number"
                className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                value={maxResults}
                onChange={e => setMaxResults(parseInt(e.target.value) || 25)}
                min={1}
                max={200}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!name.trim()}>
            {isEdit ? 'Save Changes' : 'Create Scanner'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateScannerDialog;
